import { motion } from 'framer-motion';
import { Calendar, Clock, User, ArrowRight, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

const Blog = () => {
  const articulos = [
    {
      id: 1,
      titulo: 'Luna Nueva en Capricornio: Manifestación de Objetivos',
      extracto: 'Descubre cómo aprovechar la energía de la Luna Nueva en Capricornio para establecer metas sólidas y alcanzables para el año.',
      imagen: 'https://images.unsplash.com/photo-1532693322450-2cb5c511067d?w=800&q=80',
      categoria: 'Lunaciones',
      autor: 'María Estrella',
      fecha: '15 Ene 2024',
      tiempoLectura: '5 min',
      destacado: true,
    },
    {
      id: 2,
      titulo: 'Mercurio Retrógrado: Guía de Supervivencia 2024',
      extracto: 'Todo lo que necesitas saber sobre los períodos de Mercurio retrógrado este año y cómo navegarlos con éxito.',
      imagen: 'https://images.unsplash.com/photo-1444703686981-a3abbc4d4fe3?w=800&q=80',
      categoria: 'Planetas',
      autor: 'Carlos Cosmos',
      fecha: '10 Ene 2024',
      tiempoLectura: '8 min',
      destacado: false,
    },
    {
      id: 3,
      titulo: 'Los Nodos Lunares y tu Karma',
      extracto: 'Entiende el significado de los Nodos Norte y Sur en tu carta natal y cómo indican tu propósito de vida.',
      imagen: 'https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?w=800&q=80',
      categoria: 'Astrología Avanzada',
      autor: 'Luna Vega',
      fecha: '5 Ene 2024',
      tiempoLectura: '12 min',
      destacado: false,
    },
    {
      id: 4,
      titulo: 'Signos de Agua: Intuición y Emociones',
      extracto: 'Explora las características únicas de Cáncer, Escorpio y Piscis, los signos regidos por el elemento agua.',
      imagen: 'https://images.unsplash.com/photo-1505118380757-91f5f5632de0?w=800&q=80',
      categoria: 'Signos',
      autor: 'María Estrella',
      fecha: '1 Ene 2024',
      tiempoLectura: '6 min',
      destacado: false,
    },
    {
      id: 5,
      titulo: 'Predicciones Astrológicas para 2024',
      extracto: 'Un análisis completo de los tránsitos planetarios más importantes del año y cómo afectarán a cada signo.',
      imagen: 'https://images.unsplash.com/photo-1462331940025-496dfbfc7564?w=800&q=80',
      categoria: 'Predicciones',
      autor: 'Carlos Cosmos',
      fecha: '28 Dic 2023',
      tiempoLectura: '15 min',
      destacado: false,
    },
    {
      id: 6,
      titulo: 'Cristales para cada Signo Zodiacal',
      extracto: 'Descubre qué piedras y cristales potencian mejor la energía de tu signo solar y ascendente.',
      imagen: 'https://images.unsplash.com/photo-1612817288484-6f916006741a?w=800&q=80',
      categoria: 'Bienestar',
      autor: 'Luna Vega',
      fecha: '20 Dic 2023',
      tiempoLectura: '7 min',
      destacado: false,
    },
  ];

  const articuloDestacado = articulos.find(a => a.destacado);
  const articulosRecientes = articulos.filter(a => !a.destacado);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <Badge className="mb-4 bg-purple-500/20 text-purple-200 border-purple-400/30">
            <BookOpen className="w-4 h-4 mr-2" />
            Blog Astrológico
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold text-gradient mb-4">
            Conocimiento Cósmico
          </h2>
          <p className="text-lg text-purple-100/60 max-w-2xl mx-auto">
            Explora artículos sobre astrología, bienestar espiritual y las energías del universo 
            escritos por expertos en la materia.
          </p>
        </motion.div>

        {/* Featured Article */}
        {articuloDestacado && (
          <motion.div
            className="mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <Card className="group overflow-hidden bg-purple-950/40 border-purple-500/20 hover:border-purple-400/40 transition-all duration-300">
              <div className="grid md:grid-cols-2 gap-0">
                <div className="relative h-64 md:h-auto overflow-hidden">
                  <img
                    src={articuloDestacado.imagen}
                    alt={articuloDestacado.titulo}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-950/60 to-transparent md:bg-gradient-to-r" />
                  <Badge className="absolute top-4 left-4 bg-amber-500/90 text-white border-0">
                    Destacado
                  </Badge>
                </div>
                <CardContent className="p-8 flex flex-col justify-center">
                  <Badge className="w-fit mb-4 bg-purple-500/20 text-purple-200 border-purple-400/30">
                    {articuloDestacado.categoria}
                  </Badge>
                  <h3 className="text-2xl md:text-3xl font-bold text-purple-100 mb-4 group-hover:text-purple-300 transition-colors">
                    {articuloDestacado.titulo}
                  </h3>
                  <p className="text-purple-100/60 mb-6 leading-relaxed">
                    {articuloDestacado.extracto}
                  </p>
                  <div className="flex items-center gap-4 text-sm text-purple-200/50 mb-6">
                    <span className="flex items-center gap-1">
                      <User className="w-4 h-4" />
                      {articuloDestacado.autor}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {articuloDestacado.fecha}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {articuloDestacado.tiempoLectura}
                    </span>
                  </div>
                  <Button className="w-fit bg-purple-600 hover:bg-purple-500 text-white">
                    Leer Artículo
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </CardContent>
              </div>
            </Card>
          </motion.div>
        )}

        {/* Recent Articles Grid */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {articulosRecientes.map((articulo) => (
            <motion.div key={articulo.id} variants={itemVariants}>
              <Card className="group h-full bg-purple-950/40 border-purple-500/20 overflow-hidden hover:border-purple-400/40 transition-all duration-300 hover:shadow-lg hover:shadow-purple-500/10">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={articulo.imagen}
                    alt={articulo.titulo}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-purple-950/80 to-transparent" />
                  <Badge className="absolute top-4 left-4 bg-purple-600/90 text-white border-0">
                    {articulo.categoria}
                  </Badge>
                </div>

                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-purple-100 mb-2 group-hover:text-purple-300 transition-colors line-clamp-2">
                    {articulo.titulo}
                  </h3>
                  <p className="text-sm text-purple-100/50 mb-4 line-clamp-2">
                    {articulo.extracto}
                  </p>

                  <div className="flex items-center justify-between text-sm text-purple-200/50">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {articulo.fecha}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {articulo.tiempoLectura}
                      </span>
                    </div>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-4 text-purple-300 hover:text-purple-200 hover:bg-purple-500/10 p-0"
                  >
                    Leer más
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        {/* Newsletter CTA */}
        <motion.div
          className="mt-16 text-center p-8 rounded-2xl bg-gradient-to-r from-purple-900/40 to-purple-800/40 border border-purple-500/20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h3 className="text-2xl font-bold text-purple-100 mb-2">
            Suscríbete a Nuestro Newsletter
          </h3>
          <p className="text-purple-200/60 mb-6">
            Recibe predicciones mensuales y contenido exclusivo directamente en tu correo.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              placeholder="Tu correo electrónico"
              className="flex-1 px-4 py-3 rounded-lg bg-purple-950/50 border border-purple-500/30 text-purple-100 placeholder:text-purple-400/50 focus:outline-none focus:border-purple-400"
            />
            <Button className="bg-purple-600 hover:bg-purple-500 text-white px-6">
              Suscribirme
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Blog;
